import { useLocation } from 'react-router-dom'
import { useNav } from '../lib/nav'
import { Fragment, useLayoutEffect, useRef, type ReactNode } from 'react'
import { I } from './Icons'
import { useStore } from '../state/store'
import { ToastHost } from './Toast'
import type { DayState } from '../state/day'
import { isPhone, tabBarShown } from '../lib/device'

const RAIL: { key: string; label: string; icon: (s?: number) => ReactNode; to: string; phase?: string }[] = [
  { key: 'home', label: 'Home', icon: I.home, to: '/' },
  { key: 'sell', label: 'Sell', icon: I.sell, to: '/sell' },
  { key: 'trade', label: 'Trade', icon: I.trade, to: '/trade' },
  { key: 'intake', label: 'Intake', icon: I.intake, to: '/intake' },
  { key: 'qa', label: 'QA', icon: I.qa, to: '/qa' },
  { key: 'cons', label: 'Consignors', icon: I.cons, to: '/consignors' },
  { key: 'sales', label: 'Sales', icon: I.sales, to: '/sales' },
  { key: 'time', label: 'Clock', icon: I.clock, to: '/time' },
  { key: 'close', label: 'Close out', icon: I.close, to: '/close' },
  { key: 'team', label: 'Team', icon: I.team, to: '/team' },
]

export function groupOf(path: string) {
  if (path === '/') return 'home'
  if (path.startsWith('/sell')) return 'sell'
  if (path.startsWith('/sales')) return 'sales'
  if (path.startsWith('/trade')) return 'trade'
  if (path.startsWith('/intake')) return 'intake'
  if (path.startsWith('/qa')) return 'qa'
  if (path.startsWith('/consignors')) return 'cons'
  if (path.startsWith('/inbox')) return 'home'
  if (path.startsWith('/team')) return 'team'
  if (path.startsWith('/close')) return 'close'
  if (path.startsWith('/time')) return 'time'
  return ''
}

export function Rail({ day, onLock }: { day: DayState; onLock: () => void }) {
  const nav = useNav()
  const { pathname } = useLocation()
  const group = groupOf(pathname)
  return (
    <nav className="rail" aria-label="Sections">
      <img className="rail__logo" src="/brand/star-white.png" alt="Starchive" />
      {RAIL.map((r) => (
        <Fragment key={r.key}>
        {r.key === 'qa' && <div className="rail__rule" aria-hidden="true" />}
        <button type="button" className="rail__item" aria-current={group === r.key ? 'page' : undefined}
          disabled={!!r.phase} onClick={() => nav(r.to)} title={r.phase ? `Phase ${r.phase.slice(1)}` : undefined}>
          {r.icon(24)}<span>{r.label}</span>
          {r.phase && <span className="rail__badge">{r.phase}</span>}
        </button>
        </Fragment>
      ))}
      <div className="rail__spacer" />
      <button type="button" className="rail__item rail__lock" onClick={onLock} title="Lock the app until someone opens it again">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
        <span>{day.name || 'Lock'}</span>
      </button>
    </nav>
  )
}

export function TopBar({ back, backTo, title, caption }: { back?: string; backTo?: string | (() => void); title: string; caption?: string }) {
  const nav = useNav()
  const go = () => { if (typeof backTo === 'function') backTo(); else if (backTo) nav(backTo, { back: true }) }
  // Phone: a screen the tab bar can reach does not need a back to Home
  const { pathname } = useLocation()
  const noBack = isPhone && backTo === '/' && tabBarShown(pathname)
  return (
    <header className="topbar">
      <button type="button" className="topbar__back" onClick={go} style={{ visibility: back && !noBack ? 'visible' : 'hidden' }}>{I.back()}<span>{back}</span></button>
      <div className="topbar__title">{title}</div>
      <div className="topbar__caption">{caption}</div>
    </header>
  )
}

// D3: list screens reopen where they were scrolled (for this app session); the rack and lists come back from the cache, so the
// position is there to return to. Forms and step screens are not on the list, so they always open at the top.
const KEEP_SCROLL = /^\/(sell|sales|trade|qa|consignors|customers|intake\/pending|intake\/recent)$/

export function Page({ bar, children, aside }: { bar: ReactNode; children: ReactNode; aside?: ReactNode }) {
  const { online, mode } = useStore()
  const { pathname } = useLocation()
  const col = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = col.current
    if (!el || !KEEP_SCROLL.test(pathname)) return
    const key = 'starchive.scroll:' + pathname
    let y = 0
    try { y = Number(sessionStorage.getItem(key) || 0) } catch { /* ignore */ }
    let tries = 0, raf = 0
    const put = () => { el.scrollTop = y; if (el.scrollTop < y - 2 && tries++ < 30) raf = requestAnimationFrame(put) }   // waits for the list to fill
    if (y > 0) put()
    const save = () => { try { sessionStorage.setItem(key, String(el.scrollTop)) } catch { /* ignore */ } }
    el.addEventListener('scroll', save, { passive: true })
    return () => { cancelAnimationFrame(raf); el.removeEventListener('scroll', save) }
  }, [pathname])
  return (
    <div className="main">
      {!online && <div className="offline">No connection. Intakes can't be recorded until the wifi is back.</div>}
      {mode.rehearsal && <div className="rehearsal">REHEARSAL{mode.label ? ` · ${mode.label}` : ''} · real inventory, nothing is written to Shopify or Consignify</div>}
      {bar}
      <div className="content">
        <div className="col" ref={col}>{children}</div>
        {aside && <aside className="aside">{aside}</aside>}
      </div>
      <ToastHost aside={!!aside} />
    </div>
  )
}
